/*
 * Copyright (c) 2025 Autire Technologies, LLC. All Rights Reserved.
 *
 * This product and related documentation is protected by copyright and
 * distributed under licenses restricting its use, copying, distribution and
 * decompilation. No part of this product or related documentation may be
 * reproduced in any form by any means without prior written authorization
 * from Autire Technologies, LLC and/or its licensors.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './DecryptionTool.css';
import beautify from 'js-beautify';
import { JsonView, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import { decrypt } from '../../helpers/encryption';

import { Navigate } from 'react-router-dom';
import { accountActions, commonActions } from '../../actions/';
import Spinners from '../Spinners';
import { connect } from 'react-redux';
import { withRouter } from '../../helpers';

const DecryptionTool = (props) => {
  const [value, setValue] = useState('');
  const [formattedValue, setFormattedValue] = useState('');
  const [jsonViewData, setJsonViewData] = useState(null);
  const [isJsonView, setIsJsonView] = useState(false);
  const [isJsonViewError, setIsJsonViewError] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  const localStorageUpdated = () => {
    if (!localStorage.getItem('payload')) {
      props.history.push('/login');
    }
  };

  useEffect(() => {
    window.addEventListener('storage', localStorageUpdated);
    callInitialSetup();
  }, []);

  const callInitialSetup = () => {
    props.getRestrictedUIElements(
      {
        mappingType: 'UIElementRestrictions',
        applicableYear: 'All',
      },
      (error1, response) => {
        if (response && response.length > 0 && response[0]) {
          props.getSession((err) => {
            const hasAccess = checkUserAccess(response[0], err);
            setLoading(false);
            setIsAuthenticated(hasAccess);
          });
        } else {
          setLoading(false);
          setIsAuthenticated(false);
        }
      }
    );
  };

  const checkUserAccess = (responseData, sessionError) => {
    if (sessionError) {
      return false;
    }

    const email = JSON.parse(localStorage.getItem('payload')).email;
    const decryptionTool = responseData['decryption_tool'];

    if (!decryptionTool) {
      return false;
    }

    if (decryptionTool['all']) {
      return true;
    }

    return decryptionTool['email_includes'].filter((em) => email.includes(em)).length > 0;
  };

  useEffect(() => {
    const decryptValue = decrypt(value);
    const newValue = beautify(decryptValue);
    setFormattedValue(newValue);
    try {
      const data = JSON.parse(newValue);
      setIsJsonView(true);
      setJsonViewData(data);
      setIsJsonViewError(undefined);
    } catch (error) {
      setIsJsonView(false);
      setJsonViewData(null);
      if (error.toString() !== 'SyntaxError: Unexpected end of JSON input')
        setIsJsonViewError(error.toString());
    }
  }, [value]);

  const onChange = (e) => {
    setValue(e.target.value);
  };

  const currentYear = new Date().getFullYear();

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    alert('Copy to clipboard successful');
  };
  const onClear = () => {
    setValue('');
    setFormattedValue('');
    setJsonViewData(null);
    setIsJsonView(false);
  };

  if (loading) return <Spinners />;
  if (props.loader) return <Spinners />;

  return isAuthenticated ? (
    <div id="DecryptionTool">
      <h3>Decrypt JSON</h3>
      <div className="textarea-div top">
        <div className="textarea-head">
          <label htmlFor="decrypt-input">Paste your encrypted JSON below.</label>
          <button className="copy-btn" onClick={() => copyToClipboard(value)}>
            Copy
          </button>
          <button className="clear-btn" onClick={onClear}>
            Clear
          </button>
        </div>
        <textarea value={value} onChange={onChange} id="decrypt-input"></textarea>
      </div>
      <div className="textarea-div bottom">
        <div className="textarea-head">
          <label htmlFor="decrypt-output">Decrypted JSON</label>
          <button className="copy-btn" onClick={() => copyToClipboard(formattedValue)}>
            Copy
          </button>
        </div>
        {isJsonViewError !== undefined && <p className="error-p">{isJsonViewError}</p>}
        {isJsonView ? (
          <JsonView
            data={jsonViewData}
            shouldInitiallyExpand={() => true}
            style={{ ...defaultStyles, container: 'json-view' }}
          />
        ) : (
          <textarea value={formattedValue} disabled id="decrypt-output"></textarea>
        )}
      </div>
      <div className="DecryptionTool-footer">
        <span>©{currentYear} Autire Technologies, LLC. All rights reserved.</span>
      </div>
    </div>
  ) : (
    <Navigate to="/login" state={{ from: props.location }} replace />
  );
};

function mapStateToProps(state) {
  const { loader } = state;
  return {
    loader,
  };
}

// Mapping dispatch functions to props to access easily
DecryptionTool.propTypes = {
  history: PropTypes.object.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  logout: (callback) => dispatch(accountActions.logout(callback)),
  getSession: (callback) => dispatch(accountActions.getSession(callback)),
  getRestrictedUIElements: (queryDetails, callback) =>
    dispatch(commonActions.getGlobalMapping_enc(queryDetails, callback)),
  toggleSidebar: (queryDetails, callback) =>
    dispatch(commonActions.toggleSidebar(queryDetails, callback)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DecryptionTool));
